import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import type { Sale } from "src/interfaces/sale.interface";
import type { AppState } from "src/store";
import { useOpenDialog } from "src/pages/ReportsPage/useOpenDialog";

export type ExternalEditorEntity = "sale" | "transaction";

export type OpenExternalEditorResult =
  | { status: "updated"; saved?: Promise<void> }
  | { status: "cancelled"; reason: "closed" | "interrupted" | "unmounted" };

function getSaleByIdFromList(items: Sale[], id: string): Sale | undefined {
  return items.find((sale) => sale.id === id);
}

export function useOpenExternalEditor() {
  const openDialog = useOpenDialog();
  const saleItems = useSelector((state: AppState) =>
    Object.values(state.sale.itemsById)
  );
  const saleItemsRef = useRef(saleItems);
  saleItemsRef.current = saleItems;
  const pendingSaleSaveRef = useRef<
    | {
        id: string;
        initialSale: Sale | undefined;
        resolve: () => void;
      }
    | undefined
  >(undefined);

  useEffect(() => {
    const pendingSaleSave = pendingSaleSaveRef.current;
    if (!pendingSaleSave) return;
    const currentSale = getSaleByIdFromList(saleItems, pendingSaleSave.id);
    if (currentSale === pendingSaleSave.initialSale) return;
    pendingSaleSaveRef.current = undefined;
    pendingSaleSave.resolve();
  }, [saleItems]);

  useEffect(() => {
    return () => {
      const pendingSaleSave = pendingSaleSaveRef.current;
      if (!pendingSaleSave) return;
      pendingSaleSaveRef.current = undefined;
      pendingSaleSave.resolve();
    };
  }, []);

  return useCallback(
    async (
      entity: ExternalEditorEntity,
      id: string
    ): Promise<OpenExternalEditorResult> => {
      const initialSale =
        entity === "sale"
          ? getSaleByIdFromList(saleItemsRef.current, id)
          : undefined;
      const result = await openDialog(entity, id);
      if (result.status !== "updated") {
        return result;
      }
      if (entity !== "sale") {
        return result;
      }

      const updatedSale = getSaleByIdFromList(saleItemsRef.current, id);
      if (updatedSale !== initialSale) {
        return {
          ...result,
          saved: Promise.resolve(),
        };
      }

      const saved = new Promise<void>((resolve) => {
        const previousPending = pendingSaleSaveRef.current;
        if (previousPending) previousPending.resolve();
        pendingSaleSaveRef.current = {
          id,
          initialSale,
          resolve,
        };
      });

      return {
        ...result,
        saved,
      };
    },
    [openDialog]
  );
}
